/** @jsxImportSource @emotion/react */
import { SetStateAction, useEffect, useState } from "react";

import { css } from "@emotion/react";

import Typography from "@mui/material/Typography";

import { useTutorialStore } from "@/stores/useTutorialStore";

import { useAuth } from "../../../../context/Auth/AuthProvider";
import {
  MenuAnchorEl,
  ROUTES,
  TUTORIAL_IDS,
  useMediaQueries
} from "../../../../shared";
import Avatar from "../../../../shared/components/avatars/Avatar";
import Button from "../../../Button/Button";
import useTutorialMode from "../../../TutorialMode/hooks/useTutorialMode";
import { STEP_GROUP } from "../../../TutorialMode/types";
import { useStyles } from "../../styles";

import DesktopCompanySelector from "./components/DesktopCompanySelector/DesktopCompanySelector";
import MobileCompanySelector from "./components/MobileCompanySelector/MobileCompanySelector";
import useCompanies from "./hooks/useCompanies";

interface CompanySelectorProps {
  route: ROUTES;
}

const CompanySelector = ({ route }: CompanySelectorProps) => {
  const { user } = useAuth();
  const { toSm, toMd } = useMediaQueries();
  const { companyList, getQueryData } = useCompanies(
    undefined,
    undefined,
    true
  );

  const { callTutorialWithTimeout, checkIfCorrectTutorialStep } =
    useTutorialMode();

  const isTutorialOpen = useTutorialStore(
    state => state.tutorialDetails.isTutorialOpen
  );

  const isTutorialCompanySelectorClose = useTutorialStore(
    state => state.companySelectorTutorialStatus
  );
  const setIsTutorialCompanySelectorClose = useTutorialStore(
    state => state.setCompanySelectorTutorialStatus
  );

  const [openDialog, setOpenDialog] = useState<MenuAnchorEl>(null);
  const [isMobileDialogOpen, setIsMobileDialogOpen] = useState<boolean>(false);

  const { navbarButtonTitle } = useStyles();

  useEffect(() => {
    isTutorialCompanySelectorClose &&
      openDialog &&
      handleCloseDesktopSelector();

    isTutorialCompanySelectorClose &&
      isMobileDialogOpen &&
      handleCloseMobileDialog();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTutorialCompanySelectorClose, openDialog, isMobileDialogOpen]);

  const handleCompanySelectorTutorialCall = () => {
    if (checkIfCorrectTutorialStep(STEP_GROUP.TutorialCompanySelector, 0)) {
      callTutorialWithTimeout(
        true,
        1,
        STEP_GROUP.TutorialCompanySelector,
        true,
        200
      );
    }
  };

  const closeCompanySelectorOnTutorialAction = () => {
    isTutorialCompanySelectorClose && setIsTutorialCompanySelectorClose(false);
  };

  const handleOpenDesktopSelector = (e: {
    currentTarget: SetStateAction<MenuAnchorEl>;
  }) => {
    setOpenDialog(e.currentTarget);
    handleCompanySelectorTutorialCall();
  };

  const handleCloseDesktopSelector = () => {
    setOpenDialog(null);
    closeCompanySelectorOnTutorialAction();
    getQueryData(); //refresh company list, since it might be changed when user chooses different root company but does not select any company from it before closing the selector popup
  };

  const handleOpenMobileDialog = () => {
    setIsMobileDialogOpen(true);
    handleCompanySelectorTutorialCall();
    closeCompanySelectorOnTutorialAction(); // reset isTutorialCompanySelectorClose state to close mobile dialog on previous button click
  };

  const handleCloseMobileDialog = () => {
    setIsMobileDialogOpen(false);
    closeCompanySelectorOnTutorialAction();
    getQueryData(); //refresh company list, since it might be changed when user chooses differetnt root company but does not select any company from it before closing the selector popup
  };

  const handleClickFromMd = toMd
    ? handleOpenMobileDialog
    : handleOpenDesktopSelector;

  return (
    <>
      {toSm ? (
        <Button
          data-testid={TUTORIAL_IDS.CompanySelectorButtonToSm}
          variant="iconOnly"
          size="small"
          color="white"
          onClick={handleOpenMobileDialog}
          icon={<Avatar textToCut={user?.selectedCompany.name} />}
          id={TUTORIAL_IDS.CompanySelectorButtonToSm}
          css={css({
            marginLeft: "4px"
          })}
        />
      ) : (
        <Button
          data-testid="company-selector-button-from-sm"
          id={TUTORIAL_IDS.CompanySelectorButton}
          color="white"
          variant="iconLeft"
          size="small"
          icon={<Avatar textToCut={user?.selectedCompany.name} />}
          onClick={handleClickFromMd}
        >
          <Typography variant="body2Medium" css={css(navbarButtonTitle)}>
            {user?.selectedCompany.name}
          </Typography>
        </Button>
      )}
      {toMd ? (
        <MobileCompanySelector
          route={route}
          isOpen={isMobileDialogOpen}
          onClose={isTutorialOpen ? () => null : handleCloseMobileDialog} // do not allow to close company selector during tutorial
          companies={companyList}
        />
      ) : (
        <DesktopCompanySelector
          route={route}
          onClose={isTutorialOpen ? () => null : handleCloseDesktopSelector} // do not allow to close company selector during tutorial
          anchorEl={openDialog}
          companies={companyList}
        />
      )}
    </>
  );
};

export default CompanySelector;
